import axios from 'axios';
import config from '../config';
import authService from './authService';

// Create axios instance for StanChart API
const stanchartApi = axios.create({
    baseURL: `${config.API_BASE_URL}${config.STANCHART_API_PREFIX}/${config.STANCHART_API_VERSION}`,
    timeout: config.TIMEOUTS.API_REQUEST,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    }
});

// Request interceptor: add access token
stanchartApi.interceptors.request.use(
    async (config) => {
        const token = await authService.getValidAccessToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Response interceptor: handle 401
stanchartApi.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;
            try {
                const newToken = await authService.getValidAccessToken();
                if (newToken) {
                    originalRequest.headers.Authorization = `Bearer ${newToken}`;
                    return stanchartApi(originalRequest);
                } else {
                    authService.forceLogout();
                    return Promise.reject(new Error('Authentication failed'));
                }
            } catch (refreshError) {
                console.error('Token refresh failed:', refreshError);
                authService.forceLogout();
                return Promise.reject(refreshError);
            }
        }
        return Promise.reject(error.response?.data || error.message);
    }
);

const StanChartService = {
    getConfiguration: async () => {
        try {
            const response = await stanchartApi.get(config.STANCHART.ENDPOINTS.GET_DEFAULT_CONFIG);
            return response.data;
        } catch (error) {
            console.error('Error getting StanChart configuration:', error.message);
            throw error.message;
        }
    },

    saveConfiguration: async (configData) => {
        try {

            const payload = {
                ...configData
            };

            const response = await stanchartApi.post(config.STANCHART.ENDPOINTS.CONFIGURATIONS, payload);

            return response.data;

        } catch (error) {
            console.error('Error saving StanChart configuration:', error.message);
            throw error.message;
        }
    },

    activate: async (configData) => {
        try {

            const accessToken = await authService.getValidAccessToken();

            const payload = {
                ...configData,
                accessToken
            };


            const response = await stanchartApi.post(
                config.STANCHART.ENDPOINTS.ACTIVATE,
                payload
            );

            return response.data;
        } catch (error) {
            console.error('Error generating SSH key:', error.message);
            throw error.message;
        }
    },

    testConnection: async (configData) => {

        try {

            const accessToken = await authService.getValidAccessToken();

            const payload = {
                ...configData,
                accessToken
            };

            const response = await stanchartApi.post(
                config.STANCHART.ENDPOINTS.TEST_CONNECTION,
                payload
            );

            return response.data;
        } catch (error) {
            console.error('Error generating SSH key:', error.message);
            throw error.message;
        }

    },

    getAuditTrails: async (filterData) => {
        try {

            const accessToken = await authService.getValidAccessToken();

            const payload = {
                ...filterData,
                accessToken
            };

            const response = await stanchartApi.post(
                config.STANCHART.ENDPOINTS.GET_AUDIT_TRAILS,
                payload
            );

            return response.data;

        } catch (error) {
            console.error('Error getting StanChart logs:', error.message);
            throw error.message;
        }
    },

    getStanChartBatchPayments: async (filterData) => {
        try {

            const response = await stanchartApi.get(config.STANCHART.ENDPOINTS.GET_BATCH_PAYMENTS, filterData);

            return response.data;

        } catch (error) {
            console.error('Error getting StanChart batches:', error.message);
            throw error.message;
        }
    },

    getStanChartBatchBreakdown: async (filters = {}) => {
        try {
            const response = await stanchartApi.get(config.STANCHART.ENDPOINTS.GET_TRANSACTIONS, {
                params: filters
            });
            return response.data;
        } catch (error) {
            console.error('Error getting StanChart breakdown:', error.message);
            throw error.message;
        }
    },

    generatePgpKey: async (keyData) => {
        try {
            const response = await stanchartApi.post('/generate-pgp-key', keyData);
            return response.data;
        } catch (error) {
            console.error('Error generating PGP key:', error);
            throw error;
        }
    }
};

export default StanChartService;
